<template>
  <div class="my-works-page">
    <!-- 顶部导航 -->
    <div class="header">
      <button class="back-btn" @click="$router.back()">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="title">我的作品</h1>
      <div class="header-actions">
        <button class="menu-btn">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="2" fill="currentColor"/>
            <circle cx="12" cy="5" r="2" fill="currentColor"/>
            <circle cx="12" cy="19" r="2" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 提示信息 -->
    <div class="notice-banner">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M12 16V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="8" r="1" fill="currentColor"/>
      </svg>
      <div class="notice-content">
        <p>为了保护您的隐私，我们不提供存储功能，制作记录会在创建 5 天后自动删除，请尽早提取。</p>
      </div>
    </div>

    <!-- 作品列表 -->
    <div class="works-container">
      <div v-if="loading" class="loading-state">
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <p>正在加载作品...</p>
      </div>

      <div v-else-if="works.length === 0" class="empty-state">
        <p class="empty-desc">暂无作品</p>
        <button class="create-btn" @click="goToCreate">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          去创作
        </button>
      </div>

      <div v-else class="works-list">
        <div class="work-card" v-for="work in works" :key="work.id">
          <!-- 头部信息 -->
          <div class="work-header">
            <div class="creator-info">
              <div class="avatar">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#ff9500"/>
                  <path d="M2 17L12 22L22 17" stroke="#ff9500" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="#ff9500" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="creator-text">
                <span class="creator-name">AI 图片编辑</span>
                <span class="creator-tag">AI 生成</span>
              </div>
            </div>
          </div>

          <!-- 提示词和原图缩略图 -->
          <div class="work-prompt-section">
            <!-- 显示多张原图缩略图 -->
            <template v-if="work.referenceImage">
              <div
                class="prompt-image"
                v-for="(img, index) in work.referenceImage.split(',').filter(i => i.trim())"
                :key="index"
                @click="openImagePreview(img.trim())"
              >
                <img :src="img.trim().startsWith('http') ? img.trim() : img.trim()" :alt="`参考图${index + 1}`" />
                <span class="image-label">图 {{ index + 1 }}</span>
              </div>
            </template>
            <!-- 调试信息 -->
            <span v-else style="color: #999; font-size: 12px;">无参考图</span>
            <p class="work-prompt">{{ work.prompt || '无描述' }}</p>
          </div>

          <!-- 图片展示区 -->
          <div class="work-image-section" :class="{ 'processing': work.status === 'processing' }" @click="viewWorkDetail(work)">
            <div v-if="work.status === 'processing'" class="processing-overlay">
              <div class="processing-badge">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="5,3 19,12 5,21" fill="currentColor"/>
                </svg>
                <span>创作中</span>
              </div>
              <div class="loading-animation">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
              <p class="processing-text">正在处理，预计 1-5 分钟完成</p>
            </div>
            
            <img 
              v-else-if="work.status === 'completed' && work.imageUrl" 
              :src="work.imageUrl" 
              :alt="work.prompt" 
              class="work-image"
            />
            
            <div v-else class="placeholder-image">
              <span>图片生成失败</span>
            </div>
          </div>

          <!-- 底部操作栏 -->
          <div class="work-actions" v-if="work.status === 'completed'">
            <button class="action-btn primary" @click.stop="continueEdit(work)">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 20V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18 20V4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6 20V16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              继续编辑
            </button>
            <button class="action-btn secondary" @click.stop="copyStyle(work)">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              制同款
            </button>
            <button class="action-btn more" @click.stop="showMoreOptions(work)">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="2" fill="currentColor"/>
                <circle cx="19" cy="12" r="2" fill="currentColor"/>
                <circle cx="5" cy="12" r="2" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 作品详情遮罩弹窗 -->
    <div class="detail-overlay" v-if="selectedWork" @click="closeDetail">
      <div class="detail-container" @click.stop>
        <!-- 顶部关闭按钮 -->
        <button class="close-btn" @click="closeDetail">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        
        <!-- 作品图片 -->
        <div class="detail-image-wrapper">
          <img 
            v-if="selectedWork.status === 'completed' && selectedWork.imageUrl" 
            :src="selectedWork.imageUrl" 
            :alt="selectedWork.prompt"
            class="detail-image"
          />
          <div v-else class="detail-placeholder">
            <p>{{ selectedWork.status === 'processing' ? '正在生成中...' : '生成失败' }}</p>
          </div>
          
          <!-- AI 生成水印 -->
          <div class="ai-watermark" v-if="selectedWork && selectedWork.status === 'completed'">
            AI 生成
          </div>
        </div>
        
        <!-- 底部操作栏 -->
        <div class="detail-actions" v-if="selectedWork && selectedWork.status === 'completed'">
          <button class="detail-action-btn edit" @click="continueEdit(selectedWork)">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M18 20V4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 20V16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            再次编辑
          </button>
          <button class="detail-action-btn more" @click="showMoreOptions(selectedWork)">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="2" fill="currentColor"/>
              <circle cx="19" cy="12" r="2" fill="currentColor"/>
              <circle cx="5" cy="12" r="2" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewImage" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-container" @click.stop>
        <img :src="previewImage" alt="预览图片" />
        <button class="close-preview-btn" @click="closeImagePreview">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="bottom-text">我也是有底线的 ~</div>
  </div>
</template>

<script>
export default {
  name: 'MyWorksPage',
  data() {
    return {
      works: [],
      loading: false,
      refreshInterval: null,
      selectedWork: null,
      previewImage: null, // 预览图片地址
      ws: null, // WebSocket 连接
      wsReconnectTimer: null // 重连定时器
    };
  },
  mounted() {
    this.loadWorks();
    // 连接 WebSocket
    this.connectWebSocket();
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
    }
    // 关闭 WebSocket 连接
    if (this.ws) {
      this.ws.close();
    }
  },
  methods: {
    async loadWorks() {
      this.loading = true;
      
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          this.loading = false;
          return;
        }
        
        const user = JSON.parse(userStr);
        const response = await fetch(`/api/works?userId=${user.id}`);
        const result = await response.json();
        
        if (result.success) {
          this.works = result.works;
        } else {
          console.error('加载作品失败:', result.error);
        }
      } catch (error) {
        console.error('加载作品失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    viewWorkDetail(work) {
      this.selectedWork = work;
    },
    
    closeDetail() {
      this.selectedWork = null;
    },

    openImagePreview(imageUrl) {
      this.previewImage = imageUrl;
      document.body.style.overflow = 'hidden';
    },

    closeImagePreview() {
      this.previewImage = null;
      document.body.style.overflow = '';
    },
    
    async downloadImage(work) {
      if (!work || !work.imageUrl) return;

      // 检测是否在微信环境
      const isWechat = /MicroMessenger/i.test(navigator.userAgent);

      if (isWechat) {
        // 微信环境：提示用户长按保存
        alert('请长按图片，选择「保存图片」来保存到相册');
        return;
      }

      // 非微信环境：尝试使用常规下载方式
      try {
        const response = await fetch(work.imageUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'ai-work-' + Date.now() + '.jpg';
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert('保存成功!');
      } catch (error) {
        console.error('下载失败:', error);
        alert('下载失败，请尝试长按图片保存');
      }
    },
    
    continueEdit(work) {
      this.closeDetail();
      this.$router.push({
        path: '/image-edit',
        query: {
          type: 'continue',
          prompt: work.prompt,
          image: work.imageUrl,
          model: work.model,
          ratio: work.ratio
        }
      });
    },
    
    copyStyle(work) {
      this.closeDetail();
      this.$router.push({
        path: '/image-edit',
        query: {
          type: 'copy',
          prompt: work.prompt,
          image: work.imageUrl,
          model: work.model,
          ratio: work.ratio
        }
      });
    },
    
    goToCreate() {
      this.$router.push('/image-edit');
    },
    
    showMoreOptions(work) {
      // TODO: 显示更多选项菜单
      console.log('显示更多选项:', work);
    },
    
    formatModel(model) {
      const modelMap = {
        'nano': 'Nano',
        'nano-pro': 'Nano Pro',
        'nano-banana-pro': 'Nano Banana Pro'
      };
      return modelMap[model] || model || '未知';
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;
      const month = 30 * day;
      
      if (diff < minute) {
        return '刚刚';
      } else if (diff < hour) {
        return Math.floor(diff / minute) + '分钟前';
      } else if (diff < day) {
        return Math.floor(diff / hour) + '小时前';
      } else if (diff < month) {
        return Math.floor(diff / day) + '天前';
      } else {
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    },

    // WebSocket 连接
    connectWebSocket() {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;

      const user = JSON.parse(userStr);
      // 根据当前协议选择 ws 或 wss，使用 /ws 路径
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;

      console.log('连接 WebSocket:', wsUrl);

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket 连接成功');
          // 注册用户信息
          console.log('注册用户信息:', user.id, typeof user.id);
          this.ws.send(JSON.stringify({
            type: 'register',
            userId: user.id
          }));
        };

        this.ws.onerror = (err) => {
          console.error('WebSocket 连接错误:', err);
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('收到 WebSocket 消息:', data);

            if (data.type === 'work_completed') {
              // 作品生成完成，更新列表
              this.loadWorks();
              // 可以显示提示
              if (data.message) {
                // 使用自定义提示或 alert
                console.log(data.message);
              }
            } else if (data.type === 'work_failed') {
              // 作品生成失败
              this.loadWorks();
              console.error(data.message);
            }
          } catch (err) {
            console.error('WebSocket 消息解析失败:', err);
          }
        };

        this.ws.onclose = () => {
          console.log('WebSocket 连接关闭');
          // 3秒后尝试重连
          this.wsReconnectTimer = setTimeout(() => {
            this.connectWebSocket();
          }, 3000);
        };

        this.ws.onerror = (err) => {
          console.error('WebSocket 错误:', err);
        };
      } catch (err) {
        console.error('WebSocket 连接失败:', err);
      }
    }
  }
};
</script>

<style scoped>
.my-works-page {
  min-height: 100vh;
  background: #f8f9fa;
  padding-bottom: 40px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.back-btn svg {
  width: 24px;
  height: 24px;
  stroke: #333;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin-right: 36px;
}

.header-actions {
  display: flex;
  align-items: center;
}

.menu-btn {
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.menu-btn svg {
  width: 20px;
  height: 20px;
  color: #666;
}

.notice-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  margin: 12px 16px;
  background: #fff8e6;
  border-radius: 12px;
  border: 1px solid #ffe8b8;
}

.notice-banner svg {
  width: 20px;
  height: 20px;
  color: #ff9500;
  flex-shrink: 0;
  margin-top: 1px;
}

.notice-content p {
  font-size: 13px;
  color: #ff9500;
  line-height: 1.5;
  margin: 0;
}

.works-container {
  padding: 0 16px;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.loading-dots span {
  width: 10px;
  height: 10px;
  background: #ff9500;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.loading-state p {
  color: #999;
  font-size: 14px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-desc {
  color: #999;
  font-size: 14px;
  margin-bottom: 24px;
}

.create-btn {
  padding: 12px 32px;
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(255, 149, 0, 0.3);
}

.create-btn svg {
  width: 16px;
  height: 16px;
}

.works-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.work-card {
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.work-header {
  margin-bottom: 12px;
}

.creator-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.creator-info .avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background: #fff8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.creator-info .avatar svg {
  width: 24px;
  height: 24px;
}

.creator-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.creator-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.creator-tag {
  font-size: 11px;
  color: #999;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.work-prompt-section {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.work-prompt-section .prompt-image {
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
  margin-bottom: 8px;
}

.work-prompt-section .work-prompt {
  display: inline;
  vertical-align: middle;
}

.prompt-image {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
}

.prompt-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 10px;
  text-align: center;
  padding: 2px 0;
}

.work-prompt {
  flex: 1;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin: 0;
}

.work-image-section {
  width: 100%;
  aspect-ratio: 1;
  background: #f5f5f5;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.work-image-section.processing {
  background: #f0f0f0;
  cursor: default;
}

.processing-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.processing-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
}

.processing-badge svg {
  width: 14px;
  height: 14px;
}

.loading-animation {
  display: flex;
  gap: 8px;
}

.loading-animation .dot {
  width: 10px;
  height: 10px;
  background: #ff9500;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-animation .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-animation .dot:nth-child(2) {
  animation-delay: -0.16s;
}

.processing-text {
  font-size: 13px;
  color: #999;
  margin: 0;
}

.work-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-image {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.work-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.action-btn.primary {
  flex: 0 0 auto;
  min-width: 100px;
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(255, 149, 0, 0.25);
}

.action-btn.primary svg {
  stroke: white;
}

.action-btn.secondary {
  flex: 0 0 auto;
  min-width: 100px;
  background: white;
  color: #ff9500;
  border: 1px solid #ff9500;
}

.action-btn.secondary svg {
  stroke: #ff9500;
}

.action-btn.more {
  flex: 1;
  background: #f5f5f5;
}

.action-btn.more svg {
  width: 20px;
  height: 20px;
  color: #666;
}

.action-btn:active {
  transform: scale(0.98);
}

.bottom-text {
  text-align: center;
  color: #ccc;
  font-size: 12px;
  padding: 20px;
  margin-top: 20px;
}

/* 作品详情遮罩弹窗 */
.detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.detail-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-btn svg {
  width: 20px;
  height: 20px;
  stroke: white;
}

.detail-image-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 60px 20px 100px;
}

.detail-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.detail-placeholder {
  text-align: center;
  color: #999;
  font-size: 16px;
}

.ai-watermark {
  position: absolute;
  bottom: 20px;
  right: 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 8px;
  border-radius: 4px;
}

.detail-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(10px);
  padding: 16px 20px;
  display: flex;
  gap: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.detail-action-btn.save {
  flex: 0 0 auto;
  min-width: 100px;
  background: white;
  color: #333;
}

.detail-action-btn.save svg {
  stroke: #333;
}

.detail-action-btn.edit {
  flex: 0 0 auto;
  min-width: 100px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.detail-action-btn.edit svg {
  stroke: white;
}

.detail-action-btn.more {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
}

.detail-action-btn.more svg {
  width: 20px;
  height: 20px;
  fill: white;
}

.detail-action-btn:active {
  transform: scale(0.98);
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.image-preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.image-preview-container img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.close-preview-btn {
  position: absolute;
  top: -40px;
  right: 0;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.close-preview-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-preview-btn svg {
  width: 20px;
  height: 20px;
  color: white;
}

/* 缩略图点击光标 */
.prompt-image {
  cursor: pointer;
}
</style>